/**
 * Cognito session layer (ENG-6102).
 *
 * One question for every caller that talks to the api: which token do I send?
 * `getApiToken()` answers it. While a Cognito session is active it returns the
 * Cognito ID token (it carries `custom:of_user_id` and `cognito:groups`, which
 * the api maps to the viewer); otherwise it returns the legacy HS256 token from
 * the `access_token` cache, exactly as before.
 *
 * Everything here is inert unless the `cognito-auth` GrowthBook flag is on AND
 * the `VITE_COGNITO_*` variables are set: `getApiToken()` reads the legacy cache
 * like the old per-request read did. A build without the variables never
 * configures Amplify; one with them also configures it at logout, to revoke
 * tokens a flag-on session may have left in storage.
 *
 * Leaf module on purpose: src/core/api.ts imports it, so it must not import
 * api.ts, authSession or the store.
 */
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

import config from '@/config';
import { isFeatureOn } from '@/core/growthbook';
import { clearCache, getCache } from '@/utils';

import { AUTH_TOKEN_CACHE_KEY } from './authTokenKey';

export const COGNITO_AUTH_FLAG = 'cognito-auth';

// Refresh this long before the ID token expires, so the websocket reconnects
// with a live token instead of the first request after expiry paying for it.
const REFRESH_LEAD_MS = 2 * 60 * 1000;
// After a failed timer refresh, try again this much later.
const REFRESH_RETRY_MS = 30 * 1000;
const CHANNEL_NAME = 'onfrontiers-auth';
const LOGIN_PATH = '/login';

type TokenListener = (token: string | undefined) => void;

interface State {
  // Amplify.configure has run (any configured build, flag or not).
  amplifyConfigured: boolean;
  // startCognitoSession ran with the flag on: Hub and other-tab listeners up.
  started: boolean;
  active: boolean;
  // ENG-6103: bumped whenever the session ends or is reset, so a token read
  // that started under an older session cannot write into the current one.
  generation: number;
  idToken?: string;
  // custom:of_user_id of idToken: whose identity the token carries.
  idUserId?: string;
  // ENG-6136: the legacy token boot found beside the session; see
  // ownedLegacyToken.
  legacyAtActivation?: string;
  // Reads the legacy token this tab runs on (runtime.token).
  tabToken: () => string | undefined;
  inflight: Promise<string | undefined> | null;
  inflightForced: boolean;
  signingOut: Promise<void> | null;
  expiring: Promise<void> | null;
  refreshTimer?: ReturnType<typeof setTimeout>;
  channel: BroadcastChannel | null;
  stopHub?: () => void;
  listeners: Set<TokenListener>;
  endListeners: Set<() => void>;
}

const state: State = {
  amplifyConfigured: false,
  started: false,
  active: false,
  generation: 0,
  inflight: null,
  inflightForced: false,
  signingOut: null,
  expiring: null,
  channel: null,
  tabToken: () => undefined,
  listeners: new Set(),
  endListeners: new Set(),
};

/** True when this build carries a user pool and client id. No flag check. */
export function isCognitoConfigured(): boolean {
  const { cognitoUserPoolId, cognitoClientId, cognitoRegion } = config;
  if (!cognitoUserPoolId || !cognitoClientId) return false;
  // Amplify derives the region from the pool id; a mismatch means the build
  // was given values from two different environments.
  return !cognitoRegion || cognitoUserPoolId.startsWith(`${cognitoRegion}_`);
}

/** True while the api is being sent a Cognito ID token. */
export function isCognitoSessionActive(): boolean {
  return state.active;
}

function legacyToken(): string | undefined {
  const cached = getCache(AUTH_TOKEN_CACHE_KEY) as { token?: string } | undefined;
  return cached?.token || undefined;
}

/**
 * ENG-6136: the legacy token this tab's session owns, the only one its end may
 * delete from shared storage. Without a Cognito session it is the stored one.
 * With one it is the token boot found in storage, while it is still there. A
 * token stored since belongs to an identity that replaced the session (an OTP
 * sign-in, staff login-as, possibly in another tab), and one present at a
 * sign-in later in the page to the identity signed in before it.
 */
function ownedLegacyToken(): string | undefined {
  const stored = legacyToken();
  if (!state.active) return stored;
  return stored === state.legacyAtActivation ? stored : undefined;
}

// The sub claim of a JWT (a legacy token's user id), or undefined.
function jwtSubject(token: string): string | undefined {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const sub = (JSON.parse(atob(payload)) as { sub?: unknown }).sub;
    return typeof sub === 'string' ? sub : undefined;
  } catch {
    return undefined;
  }
}

/** Names a token in a logout message without carrying it (32-bit FNV-1a). */
export function tokenTag(token: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < token.length; i += 1) {
    hash = Math.imul(hash ^ token.charCodeAt(i), 0x01000193);
  }
  return (hash >>> 0).toString(16);
}

// A failed forced refresh re-arms the timer, or the token would only be
// refreshed again by the next request. One that finds no tokens (refresh
// token expired or revoked) ends the session, as getApiToken() does.
function scheduleRefresh(delayMs: number): void {
  clearTimeout(state.refreshTimer);
  const generation = state.generation;
  state.refreshTimer = setTimeout(() => {
    if (generation !== state.generation) return;
    refresh(true).then(
      (token) => {
        if (generation !== state.generation) return;
        if (!token && state.active) void expireCognitoSession();
      },
      () => {
        if (generation !== state.generation) return;
        if (state.active) scheduleRefresh(REFRESH_RETRY_MS);
      }
    );
  }, delayMs);
}

function setIdToken(token: string | undefined, expiresAtSec?: number, userId?: string): void {
  clearTimeout(state.refreshTimer);
  if (token && expiresAtSec) {
    scheduleRefresh(Math.max(expiresAtSec * 1000 - Date.now() - REFRESH_LEAD_MS, 0));
  }
  if (token === state.idToken) return;
  state.idToken = token;
  state.idUserId = token ? userId : undefined;
  state.listeners.forEach((listener) => listener(token));
}

async function loadIdToken(forceRefresh: boolean): Promise<string | undefined> {
  const generation = state.generation;
  const { tokens } = await fetchAuthSession({ forceRefresh });
  // A logout or a reset raced this refresh: drop the late result rather than
  // install an old session's token or re-arm its timer.
  if (generation !== state.generation || !state.active) return undefined;
  const idToken = tokens?.idToken;
  if (!idToken) return undefined;
  const claim = idToken.payload['custom:of_user_id'];
  const userId = typeof claim === 'string' ? claim : undefined;
  // ENG-6136: a token for another user (a sign-in in another tab, in the
  // shared Amplify storage) is a new session: origins captured under the old
  // one must stop matching, and it owns no legacy token boot found for the
  // previous user. A refresh of the same user's token is neither.
  if (state.idUserId && userId !== state.idUserId) {
    state.generation += 1;
    state.legacyAtActivation = undefined;
  }
  // Nor one boot found that another user left beside it (legacy A kept by an
  // in-page switch to Cognito B, then a reload). Checked on every read, so a
  // boot whose first read failed is covered by the read that succeeds.
  const owner = state.legacyAtActivation && jwtSubject(state.legacyAtActivation);
  if (owner && userId && owner !== userId) state.legacyAtActivation = undefined;
  setIdToken(idToken.toString(), idToken.payload.exp, userId);
  return state.idToken;
}

// Single flight: concurrent callers share one fetchAuthSession, so a burst of
// queries at the moment the token expires costs one refresh, not one each. A
// forced refresh joins only forced work: a plain read in flight may hand back
// the very token the api just refused, so it waits for that read and then
// starts (or joins) a forced one.
function refresh(forceRefresh = false): Promise<string | undefined> {
  if (state.inflight && forceRefresh && !state.inflightForced) {
    return state.inflight.catch(() => undefined).then(() => refresh(true));
  }
  if (!state.inflight) {
    state.inflightForced = forceRefresh;
    const read: Promise<string | undefined> = loadIdToken(forceRefresh).finally(() => {
      // A reset may already have replaced this read with a newer one.
      if (state.inflight !== read) return;
      state.inflight = null;
      state.inflightForced = false;
    });
    state.inflight = read;
  }
  return state.inflight;
}

/**
 * For a read that belonged to a session since ended or replaced: the current
 * Cognito session's token, or none. Never the legacy token, which belongs to
 * a different identity than the request was built for.
 */
function currentSessionToken(): Promise<string | undefined> {
  return state.active ? getApiToken() : Promise.resolve(undefined);
}

/**
 * The bearer token for the api: the Cognito ID token while a Cognito session is
 * active, else the legacy HS256 token. A refresh that ends the session (refresh
 * token expired or revoked) logs the user out. A transient refresh failure
 * rejects, which fails the request like a network error and keeps the session.
 */
export async function getApiToken(): Promise<string | undefined> {
  if (!state.active) return legacyToken();
  const generation = state.generation;
  let token: string | undefined;
  try {
    token = await refresh();
  } catch (err) {
    if (generation !== state.generation) return currentSessionToken();
    throw err;
  }
  if (generation !== state.generation) return currentSessionToken();
  if (!token && state.active) {
    void expireCognitoSession();
  }
  return token;
}

/**
 * Force one refresh (the api refused the token as expired or invalid). Shares
 * an in-flight refresh. Resolves undefined when there is no Cognito session.
 */
export async function forceRefreshApiToken(): Promise<string | undefined> {
  if (!state.active) return undefined;
  const generation = state.generation;
  try {
    const token = await refresh(true);
    return generation === state.generation ? token : forceRefreshApiToken();
  } catch (err) {
    if (generation !== state.generation) return forceRefreshApiToken();
    throw err;
  }
}

/** The Cognito session ended while a signup funnel still needed it. */
export class CognitoSessionEndedError extends Error {
  constructor() {
    super('The Cognito session has ended');
    this.name = 'CognitoSessionEndedError';
  }
}

/**
 * Which session a signup-funnel mutation was sent under (ENG-6106). Capture it
 * before the mutation and hand it to sessionTokenOr after, so a session that
 * ended or was replaced while the mutation was in flight is not revived.
 */
export interface SessionOrigin {
  cognito: boolean;
  generation: number;
}

export function captureSessionOrigin(): SessionOrigin {
  return { cognito: state.active, generation: state.generation };
}

/**
 * Throws CognitoSessionEndedError when origin was a Cognito session that has
 * since ended or been replaced. An origin on legacy authentication passes.
 */
export function assertSessionOrigin(origin: SessionOrigin): void {
  if (origin.cognito && (origin.generation !== state.generation || !state.active)) {
    throw new CognitoSessionEndedError();
  }
}

/**
 * The token to re-authenticate the viewer with after a signup-funnel step
 * (ENG-6106). While a Cognito session is active it is the Cognito ID token,
 * read now, whatever token the api minted: the session is the credential, and
 * a minted HS256 token would put a second one in storage. Otherwise the
 * caller's token, unchanged, but only for an operation that started on legacy
 * authentication: one that started on a Cognito session that has since ended
 * or changed throws CognitoSessionEndedError, so the minted token cannot
 * restore an identity the session end took away. Throws the same when the
 * session ends under the read (getApiToken has already sent the user to
 * /login).
 */
export function sessionTokenOr(fallback: string, origin: SessionOrigin): Promise<string>;
export function sessionTokenOr(
  fallback: string | null | undefined,
  origin: SessionOrigin
): Promise<string | null>;
export async function sessionTokenOr(
  fallback: string | null | undefined,
  origin: SessionOrigin
): Promise<string | null> {
  assertSessionOrigin(origin);
  if (!state.active) return fallback ?? null;
  const generation = state.generation;
  const token = await getApiToken();
  if (!token || generation !== state.generation) throw new CognitoSessionEndedError();
  return token;
}

/** Last Cognito ID token seen, for synchronous readers (the websocket). */
export function peekCognitoIdToken(): string | undefined {
  return state.active ? state.idToken : undefined;
}

/** The platform user id the current Cognito ID token carries. */
export function peekCognitoUserId(): string | undefined {
  return state.active ? state.idUserId : undefined;
}

/** How to read the legacy token this tab runs on (index.tsx: runtime.token). */
export function setTabTokenReader(read: () => string | undefined): void {
  state.tabToken = read;
}

/** Called with the new token whenever the Cognito ID token changes. */
export function onApiTokenChange(listener: TokenListener): () => void {
  state.listeners.add(listener);
  return () => state.listeners.delete(listener);
}

/**
 * Called when the Cognito session ends in this tab without going through
 * logout() (expiry, or a logout in another tab), so the app can drop this
 * tab's copy of the identity: runtime.token, the websocket. Not the shared
 * legacy token, which this module clears itself (ENG-6136).
 */
export function onCognitoSessionEnd(listener: () => void): () => void {
  state.endListeners.add(listener);
  return () => state.endListeners.delete(listener);
}

function notifySessionEnd(): void {
  state.endListeners.forEach((listener) => listener());
}

function activate(legacyOwned?: string): Promise<string | undefined> {
  if (!state.active) state.legacyAtActivation = legacyOwned;
  state.active = true;
  state.signingOut = null;
  state.expiring = null;
  return refresh();
}

function redirectToLogin(): void {
  if (window.location.pathname.startsWith(LOGIN_PATH)) return;
  window.location.assign(LOGIN_PATH);
}

function clearLegacySession(): void {
  clearCache('session');
  clearCache(AUTH_TOKEN_CACHE_KEY);
  clearCache('user_context');
}

// Clears the legacy session from shared storage unless it holds a token other
// than the one the ending session owned (ownedTag).
function clearLegacySessionUnlessReplaced(ownedTag: string | null | undefined): void {
  const stored = legacyToken();
  if (stored && tokenTag(stored) !== ownedTag) return;
  clearLegacySession();
}

function deactivate(): void {
  state.active = false;
  state.generation += 1;
  // A read in flight belongs to the session just ended; the next activation
  // starts its own instead of joining it.
  state.inflight = null;
  state.inflightForced = false;
  setIdToken(undefined);
}

/**
 * ENG-6103: forget this tab's session (active flag, cached ID token, refresh
 * timer) without signing out of Cognito or telling other tabs. For a password
 * sign-in that replaces the current session: until the new one completes, no
 * request or scheduled refresh may find the old tokens gone and expire it.
 */
export function resetCognitoSessionLocally(): void {
  deactivate();
}

/**
 * ENG-6103: another identity is taking over this tab (an OTP sign-in after a
 * Cognito sign-in whose viewer fetch failed). Reset locally and clear the
 * Amplify tokens, so a reload cannot revive the Cognito session, but tell no
 * other tab: their logout handler would wipe the legacy token now being set.
 */
export async function discardCognitoSession(): Promise<void> {
  deactivate();
  configureAmplify();
  await signOut().catch(() => undefined);
}

function configureAmplify(): void {
  if (state.amplifyConfigured) return;
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.cognitoUserPoolId as string,
        userPoolClientId: config.cognitoClientId as string,
      },
    },
  });
  state.amplifyConfigured = true;
}

/**
 * Sign out of Cognito (revoking the refresh token) and tell the other tabs.
 * Idempotent: repeated calls share one sign-out. Signs out whenever the build
 * carries VITE_COGNITO_*, flag or not, so tokens left in storage by a session
 * started while the flag was on (or before GrowthBook timed out) are revoked.
 * Resolves at once in a build without them, so that logout is unchanged.
 */
export function endCognitoSession(): Promise<void> {
  return signOutAndTell(ownedLegacyToken());
}

// ENG-6136: the message names the legacy token the ending session owned, so a
// tab that reads it late deletes nothing stored since.
function signOutAndTell(owned: string | undefined): Promise<void> {
  if (!isCognitoConfigured()) return Promise.resolve();
  if (!state.signingOut) {
    const message = { type: 'logout', owned: owned ? tokenTag(owned) : null };
    deactivate();
    const broadcast = state.started;
    configureAmplify();
    state.signingOut = signOut()
      .catch((err: unknown) => {
        // Revocation is best effort; the local tokens are already unusable to us.
        console.warn('Cognito sign-out failed:', err instanceof Error ? err.name : 'unknown');
      })
      .then(() => {
        // Only now: told earlier, another tab would reload /login and pick the
        // session back up from storage that signOut() had not cleared yet.
        if (broadcast) postToOtherTabs(message);
      });
  }
  return state.signingOut;
}

/**
 * Session over (refresh failed, or the api refused the token): log out, then
 * go to /login once the sign-out has finished. Idempotent: the link that saw
 * the refusal and the GraphQLClient wrapper that rethrows it share one run.
 */
export function expireCognitoSession(): Promise<void> {
  if (!state.expiring) {
    // ENG-6136: a refusal noticed after the session already ended (logout,
    // login-as, an OTP takeover) is not an expiry.
    if (!state.active) return Promise.resolve();
    const owned = ownedLegacyToken();
    clearLegacySessionUnlessReplaced(owned && tokenTag(owned));
    notifySessionEnd();
    state.expiring = signOutAndTell(owned).then(redirectToLogin);
  }
  return state.expiring;
}

/** True from the moment a Cognito session starts expiring until the redirect. */
export function isCognitoSessionExpiring(): boolean {
  return state.expiring !== null;
}

function listenToAmplify(): void {
  state.stopHub = Hub.listen('auth', ({ payload }) => {
    switch (payload.event) {
      case 'signedIn':
        void activate().catch(() => undefined);
        break;
      case 'tokenRefresh':
        if (state.active) void refresh().catch(() => undefined);
        break;
    }
  });
}

function postToOtherTabs(message: { type: string; owned: string | null }): void {
  try {
    state.channel?.postMessage(message);
  } catch {
    // The channel was closed (page unloading); the other tabs reload anyway.
  }
}

function listenToOtherTabs(): void {
  if (typeof BroadcastChannel === 'undefined') return;
  state.channel = new BroadcastChannel(CHANNEL_NAME);
  state.channel.onmessage = (event: MessageEvent<{ type?: string; owned?: string | null }>) => {
    if (event.data?.type !== 'logout') return;
    // ENG-6136: the message may arrive after login-as or an OTP sign-in
    // stored a replacement, which must survive it.
    clearLegacySessionUnlessReplaced(event.data.owned);
    // A tab off Cognito still running on the stored token is on another
    // identity than the one logged out. Any other tab drops its own copy and
    // reloads, onto whatever identity storage now holds.
    const stored = legacyToken();
    if (!state.active && stored && stored === state.tabToken()) return;
    deactivate();
    notifySessionEnd();
    redirectToLogin();
  };
}

/**
 * True when password login should go through Cognito (ENG-6103): the build is
 * configured and `cognito-auth` is on. Read at submit time, not at boot, so a
 * flag that loaded after the boot timeout still takes effect.
 */
export function isCognitoLoginEnabled(): boolean {
  return isCognitoConfigured() && isFeatureOn(COGNITO_AUTH_FLAG);
}

/**
 * Configure Amplify and the listeners once. Returns false (and does nothing)
 * unless Cognito login is enabled. Boot calls it through startCognitoSession;
 * the sign-in wrapper calls it too, for a page whose flags loaded late.
 */
export function ensureCognitoConfigured(): boolean {
  if (!isCognitoLoginEnabled()) return false;
  configureAmplify();
  if (!state.started) {
    state.started = true;
    listenToAmplify();
    listenToOtherTabs();
  }
  return true;
}

/**
 * Make the session active now and resolve with the ID token, without waiting
 * for the Hub `signedIn` event. The sign-in wrapper calls it after a completed
 * sign-in so the requests that follow already send the Cognito token.
 */
export async function activateCognitoSession(): Promise<string | undefined> {
  const generation = state.generation;
  const token = await activate();
  // A late read from an activation a reset already superseded must not tear
  // down the session that replaced it.
  if (!token && generation === state.generation) deactivate();
  return token;
}

/**
 * Boot: configure Amplify and pick up a session left in storage. Does nothing
 * (and returns false) unless the build is configured and `cognito-auth` is on,
 * so call it after GrowthBook has loaded. Safe to call twice.
 */
export async function startCognitoSession(): Promise<boolean> {
  if (!ensureCognitoConfigured()) return false;
  try {
    // Amplify reports no tokens: no session, the legacy path is in charge.
    const legacy = legacyToken();
    if (!(await activate(legacy))) deactivate();
  } catch {
    // Tokens exist but the refresh failed transiently. The session stays
    // active and the next getApiToken() retries the refresh.
  }
  return state.active;
}

/** Test seam: forget everything, as a fresh page load would. */
export function resetCognitoSessionForTests(): void {
  clearTimeout(state.refreshTimer);
  state.channel?.close();
  state.stopHub?.();
  Object.assign(state, {
    amplifyConfigured: false,
    started: false,
    active: false,
    idToken: undefined,
    idUserId: undefined,
    legacyAtActivation: undefined,
    tabToken: () => undefined,
    inflight: null,
    inflightForced: false,
    signingOut: null,
    expiring: null,
    refreshTimer: undefined,
    channel: null,
    stopHub: undefined,
  });
  state.listeners.clear();
  state.endListeners.clear();
}
